import { useAssetVizInternal } from 'features/assetViz/AssetVizInternalProvider'
import { Vec2, Vec4 } from 'features/assetViz/utils/helpers'
import { overlayMaterialDefines } from 'features/assetViz/utils/renderingDefines'
import { useLoadTexture } from 'features/assetViz/utils/useLoadTexture'
import React from 'react'
import * as THREE from 'three'
import { GeckoComponentRect } from 'types'

import frag from './basicTexture.frag'
import vert from './surface.vert'

// For live-reload of the shader file on save.
const key = THREE.MathUtils.generateUUID()

/**
 The material used to render a photogrammetry texture on the component's surface
 * @param chunkBounds         The bounds of the chunk, in component coordinates
 * @param doWrapX             Whether this chunk wraps in the x direction
 * @param doWrapY             Whether this chunk wraps in the y direction
 * @constructor
 */
export const PhotogrammetryMaterial: React.FC<{
  chunkBounds: GeckoComponentRect
  doWrapX: boolean
  doWrapY: boolean
}> = ({ chunkBounds, doWrapX, doWrapY }) => {
  //----------------------------------------------------------------------------
  const { clipConfig } = useAssetVizInternal()

  const uniforms = React.useMemo(
    () => ({
      // Component coords
      u_chunk_rect: { value: [0, 0, 0, 0] as Vec4 },
      u_wrap_dimensions: { value: [0, 0] as Vec2 },
      // Texture
      u_tex: { value: null as THREE.Texture | null },
      u_tex_rect: { value: [0, 0, 0, 0] as Vec4 },
      // Clipping
      u_clip_rect: { value: [0, 0, 0, 0] as Vec4 },
    }),
    []
  )

  //----------------------------------------------------------------------------

  React.useEffect(() => {
    uniforms.u_chunk_rect.value = [
      chunkBounds.start.x.num,
      chunkBounds.start.y.num,
      chunkBounds.end.x.num,
      chunkBounds.end.y.num,
    ]
    uniforms.u_wrap_dimensions.value = [
      doWrapX ? chunkBounds.width.num : -1,
      doWrapY ? chunkBounds.height.num : -1,
    ]
  }, [uniforms, chunkBounds, doWrapX, doWrapY])

  React.useEffect(() => {
    uniforms.u_clip_rect.value = [
      clipConfig.unscaledRangeX?.[0] ?? -1e20,
      clipConfig.unscaledRangeY?.[0] ?? -1e20,
      clipConfig.unscaledRangeX?.[1] ?? 1e20,
      clipConfig.unscaledRangeY?.[1] ?? 1e20,
    ]
  }, [uniforms, clipConfig.unscaledRangeX, clipConfig.unscaledRangeY])

  //----------------------------------------------------------------------------

  const {
    setPhotogrammetryTextureLoading,
    activePhotogrammetryTexture,
    hasPhotogrammetryTextureUpdates,
  } = useAssetVizInternal()
  const { texture, isLoading } = useLoadTexture(
    activePhotogrammetryTexture?.texture_url ?? ''
  )

  React.useEffect(() => {
    setPhotogrammetryTextureLoading?.(isLoading)
  }, [isLoading, setPhotogrammetryTextureLoading])

  React.useEffect(() => {
    if (!texture) return
    if (hasPhotogrammetryTextureUpdates) {
      // Pretend to use hasTextureUpdates so it will re-render while adjusting the rect
    }
    texture.generateMipmaps = false
    texture.wrapS = THREE.ClampToEdgeWrapping
    texture.wrapT = THREE.ClampToEdgeWrapping
    texture.magFilter = THREE.LinearFilter
    texture.minFilter = THREE.LinearFilter
    texture.needsUpdate = true

    uniforms.u_tex.value = texture
    if (activePhotogrammetryTexture) {
      uniforms.u_tex_rect.value = [
        activePhotogrammetryTexture.rect.start.x.num,
        activePhotogrammetryTexture.rect.start.y.num,
        activePhotogrammetryTexture.rect.end.x.num,
        activePhotogrammetryTexture.rect.end.y.num,
      ]
    }
  }, [
    texture,
    activePhotogrammetryTexture?.rect,
    uniforms.u_tex,
    uniforms.u_tex_rect,
    activePhotogrammetryTexture,
    hasPhotogrammetryTextureUpdates,
  ])

  //----------------------------------------------------------------------------

  return (
    <shaderMaterial
      key={key}
      vertexShader={vert}
      fragmentShader={frag}
      uniforms={uniforms}
      {...overlayMaterialDefines}
    />
  )
}
